import { Component, Vue, Watch } from 'vue-property-decorator';
import { formatDateToLocalDateTime } from '@/helpers';
import { HRMAccountTeamDTO, HRMActivityDTO, ProjectDTO, ProjectRequestDTO } from '@/models';
import { buildProjectRequestData, fetchAllActivities } from '@/services';

@Component
export default class LinkHRMProjectDialog extends Vue {
  $refs!: {
    formRef: HTMLFormElement;
  };

  private hrmProjectOptions = [] as HRMAccountTeamDTO[];
  private selectedHrmProject = {} as HRMAccountTeamDTO;
  private hrmActivityOptions = [] as HRMActivityDTO[];
  private areHRMProjectsFetching = true;
  private areActivitiesFetching = false;
  private projectDataTemp = {} as ProjectRequestDTO;
  private currentHrmProject: HRMAccountTeamDTO;
  private autocompleteProjectProps = {
    dense: true,
    outlined: true,
    require: true,
    returnObject: true,
    itemText: 'projectName'
  };
  private autocompleteActivitiesProps = {
    dense: true,
    outlined: true,
    require: true,
    multiple: true,
    itemText: 'name',
    itemValue: 'name'
  };
  private rules = {
    required: (v: any) => !!v || 'Required'
  };

  get projectData(): ProjectDTO {
    return this.$store.getters['project/project'];
  }

  async onCloseDialog(): Promise<void> {
    this.$emit('onCloseDialog', true);
    await this.onSelectProjectChanged(this.currentHrmProject);
    this.projectDataTemp = await buildProjectRequestData(this.projectData);
  }

  @Watch('projectData')
  async syncHrmProjectData(): Promise<void> {
    this.projectDataTemp = await buildProjectRequestData(this.projectData);
    const hrmProjectId = this.projectDataTemp.hrmProjectId;
    if (hrmProjectId) {
      const currentHrmProject =
        this.hrmProjectOptions.find(project => project.projectId === hrmProjectId) || ({} as HRMAccountTeamDTO);
      await this.onSelectProjectChanged(currentHrmProject, true);
    }
  }

  async onSelectProjectChanged(project: HRMAccountTeamDTO, keepSelectedActivities = false): Promise<void> {
    this.selectedHrmProject = project || ({} as HRMAccountTeamDTO);
    if (!project?.projectId) {
      this.hrmActivityOptions = [];
      this.projectDataTemp.activities = [];
      this.projectDataTemp.hrmProjectId = undefined;
      this.projectDataTemp.hrmProjectName = undefined;
      this.projectDataTemp.clientId = undefined;
      return;
    }

    this.areActivitiesFetching = true;
    this.hrmActivityOptions = [];
    this.projectDataTemp.activities = keepSelectedActivities ? this.projectDataTemp.activities || [] : [];
    this.projectDataTemp.hrmProjectId = project.projectId;
    this.projectDataTemp.hrmProjectName = this.buildHrmProjectName(project);
    this.projectDataTemp.clientId = project.clientId;

    const { data } = await fetchAllActivities();
    this.hrmActivityOptions = data.filter(activity => activity.projectId === project.projectId);
    this.areActivitiesFetching = false;
  }

  private buildHrmProjectName(project: HRMAccountTeamDTO): string {
    return `${project.clientName} - ${project.projectName}`;
  }

  async onLinkHrmProject(): Promise<void> {
    const updateData = {
      ...this.projectDataTemp,
      startDate: formatDateToLocalDateTime(this.projectData.startDate),
      endDate: formatDateToLocalDateTime(this.projectData.startDate)
    };
    await this.$store.dispatch('project/asyncUpdateProjectInformation', updateData);
    this.currentHrmProject = this.selectedHrmProject;
    await this.onCloseDialog();
  }

  async created(): Promise<void> {
    this.hrmProjectOptions =
      this.$store.getters['project/hrmProjects']?.length > 0
        ? this.$store.getters['project/hrmProjects']
        : await this.$store.dispatch('project/fetchHRMProjects');
    await this.syncHrmProjectData();
    this.areHRMProjectsFetching = false;
  }
}
